// Piezas compartidas de la versión móvil (/m). Mismo aire que el resto de LGR
// (tokens + mono para identificadores), pero en formato card apilada apta para
// el pulgar: la tabla densa del desktop acá se vuelve una lista de tarjetas.
import { useState } from 'react'
import { fotoArticuloUrl } from '../../api/notasVenta'
import { colors, font, text } from '../../design/tokens'
import { fmtMonto, fmtFecha, fmtFechaHora } from '../../utils/fecha'

// Mismo formato de importe que el resto de la app (fmtMonto canónico, en-US):
// el importe se tiene que leer IGUAL en el celular que en el escritorio.
export const fmtMoney = (n) => {
  const s = fmtMonto(n)
  return s === '—' ? s : '$' + s
}

// Título de pantalla del móvil (unificado: 20px en todas las secciones)
export function Titulo({ children }) {
  return <h1 style={{ margin: '4px 0 10px', fontSize: 20, fontFamily: font.sans, color: colors.text }}>{children}</h1>
}

// Link a Google Maps de una dirección (patrón compartido por los detalles)
export function LinkMapa({ dir }) {
  if (!dir) return null
  return (
    <a
      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(dir)}`}
      target="_blank" rel="noopener noreferrer"
      style={{ display: 'inline-block', marginTop: 8, fontSize: 12.5, fontWeight: 600, color: colors.link, textDecoration: 'none' }}
    >
      🗺 Ver en mapa
    </a>
  )
}

// ─── Contenedores ────────────────────────────────────────────────────

export function Card({ onClick, children, style }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 14,
        padding: '12px 14px', cursor: onClick ? 'pointer' : 'default',
        WebkitTapHighlightColor: 'rgba(15,23,42,0.06)', ...style,
      }}
    >
      {children}
    </div>
  )
}

export function Seccion({ titulo, children, style }) {
  return (
    <section style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 14, padding: 14, marginTop: 12, ...style }}>
      {titulo && (
        <h2 style={{ margin: '0 0 10px', fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {titulo}
        </h2>
      )}
      {children}
    </section>
  )
}

// Línea label → valor de un detalle (dl compacto)
export function Dato({ label, children, mono }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 0', borderBottom: `1px solid ${colors.borderSoft}` }}>
      <span style={{ fontSize: text.sec, color: colors.textSec, flexShrink: 0 }}>{label}</span>
      <span style={{
        fontSize: text.body, color: colors.text, textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere',
        ...(mono ? { fontFamily: font.mono, fontVariantNumeric: 'tabular-nums' } : {}),
      }}>
        {children ?? '—'}
      </span>
    </div>
  )
}

// ─── Filtros ─────────────────────────────────────────────────────────

export function Buscador({ value, onChange, placeholder }) {
  return (
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%', boxSizing: 'border-box', padding: '10px 14px',
        border: `1px solid ${colors.border}`, borderRadius: 12, background: colors.bg,
        fontSize: 15, fontFamily: font.sans, color: colors.text, outline: 'none',
      }}
    />
  )
}

// Chips multi-select con scroll horizontal (mismo color que el badge del estado)
export function ChipsScroll({ options, selected, onToggle }) {
  return (
    <div style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '8px 0 4px', WebkitOverflowScrolling: 'touch' }}>
      {options.map((o) => {
        const on = selected.includes(o.value)
        return (
          <button
            key={o.value}
            onClick={() => onToggle(o.value)}
            style={{
              flexShrink: 0, padding: '6px 12px', borderRadius: 999, cursor: 'pointer',
              border: `1px solid ${on ? (o.color || colors.link) : colors.border}`,
              background: on ? (o.bg || colors.linkBg) : colors.bg,
              color: on ? (o.color || colors.link) : colors.textSec,
              fontSize: 12, fontWeight: 600, fontFamily: font.sans, whiteSpace: 'nowrap',
            }}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

// ─── Estados de carga ────────────────────────────────────────────────

export function Cargando() {
  return <div style={{ padding: 40, textAlign: 'center', color: colors.textMeta, fontSize: 13 }}>Cargando…</div>
}

export function ErrorMsg({ children }) {
  return <div style={{ padding: 30, textAlign: 'center', color: colors.err, fontSize: 13 }}>{children}</div>
}

export function Vacio({ children = 'Sin resultados' }) {
  return <div style={{ padding: 30, textAlign: 'center', color: colors.textMeta, fontSize: 13 }}>{children}</div>
}

export function BotonCargarMas({ onClick, cargando }) {
  return (
    <button
      onClick={onClick}
      disabled={cargando}
      style={{
        width: '100%', padding: '12px 0', marginTop: 10, borderRadius: 12,
        border: `1px solid ${colors.border}`, background: colors.bg, color: colors.link,
        fontSize: 13.5, fontWeight: 600, fontFamily: font.sans, cursor: 'pointer',
        opacity: cargando ? 0.6 : 1,
      }}
    >
      {cargando ? 'Cargando…' : 'Cargar más'}
    </button>
  )
}

// Badge chico de tipo de documento (Re azul / NE violeta) + número en mono.
// Misma geometría que los badges del desktop (11px / 600).
export function DocTag({ tipo, numero }) {
  const esNe = tipo === 'ne'
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
      <span style={{
        padding: '1px 6px', borderRadius: 4, fontSize: text.meta, fontWeight: 600, letterSpacing: '0.02em',
        background: esNe ? colors.t80Bg : colors.t90Bg, color: esNe ? colors.t80Text : colors.t90Text,
      }}>
        {esNe ? 'NE' : 'Re'}
      </span>
      <span style={{ fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontSize: 12.5, color: colors.text, whiteSpace: 'nowrap' }}>
        {numero || '—'}
      </span>
    </span>
  )
}

// ─── Botón primario del móvil (full-width, target táctil grande) ─────
// UNA definición para toda la sección /m: mismo azul primario del sistema
// (colors.primary = el de Boton.jsx), misma geometría en todas las pantallas.
export function BotonPrimario({ children, style, ...props }) {
  return (
    <button
      {...props}
      style={{
        width: '100%', boxSizing: 'border-box', padding: '15px 0', borderRadius: 12,
        border: 'none', background: colors.primary, color: '#fff',
        fontSize: 15.5, fontWeight: 700, fontFamily: font.sans, cursor: 'pointer',
        ...style,
      }}
    >
      {children}
    </button>
  )
}

// Banner de error inline (arriba del contenido; distinto del ErrorMsg centrado)
export function ErrorBox({ children }) {
  if (!children) return null
  return (
    <div style={{
      background: colors.errBg, color: colors.err, border: '1px solid #fecaca',
      borderRadius: 10, padding: '10px 12px', fontSize: 13, marginBottom: 10, fontFamily: font.sans,
    }}>
      {children}
    </div>
  )
}

// Tabs segmentados (negro = activo) — presets de fecha, solapas de reparto
export function TabsSegmento({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
      {options.map((o) => {
        const on = value === o.k
        return (
          <button key={o.k} onClick={() => onChange(o.k)}
            style={{
              flex: 1, padding: '11px 0', borderRadius: 10,
              border: `1px solid ${on ? colors.text : colors.border}`,
              background: on ? colors.text : colors.bg, color: on ? colors.bg : colors.textSec,
              fontSize: 12.5, fontWeight: 600, fontFamily: font.sans, cursor: 'pointer', whiteSpace: 'nowrap',
            }}>
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

// ─── Viajes de un documento (remito / NE) ────────────────────────────
// Cada viaje = una fila de salida_documentos: con qué salida salió, qué
// fletero lo llevó y cómo terminó. Es la vista "entregas" del documento.
// (Exportada: el detalle de salida usa la misma paleta por parada.)
export const ENTREGA_CFG = {
  entregado:    { icono: '✓', color: colors.ok, bg: colors.okBg, label: 'Entregado' },
  no_entregado: { icono: '✕', color: colors.err, bg: colors.errBg, label: 'No entregado' },
  reagendado:   { icono: '↻', color: '#9a3412', bg: '#ffedd5', label: 'Reagendado' },
}
const VIAJE_CFG = ENTREGA_CFG

export function ViajesSeccion({ docs, onVerSalida }) {
  if (!docs || docs.length === 0) return null
  return (
    <Seccion titulo={`Viajes (${docs.length})`}>
      {docs.map((d, i) => {
        const cfg = VIAJE_CFG[d.estado_entrega]
        const enCurso = !d.estado_entrega && !d.liberado_at
        return (
          <div
            key={d.id || i}
            onClick={onVerSalida && d.salida?.id ? () => onVerSalida(d.salida.id) : undefined}
            style={{
              padding: '8px 0', cursor: onVerSalida && d.salida?.id ? 'pointer' : 'default',
              borderBottom: i < docs.length - 1 ? `1px solid ${colors.borderSoft}` : 'none',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontSize: 12.5, color: colors.text, flexShrink: 0 }}>
                Salida #{d.salida?.id || d.salida_id}
              </span>
              <span style={{ fontSize: text.sec, color: colors.textSec, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {d.salida?.fletero ? `${d.salida.fletero.interno ? '🏬' : '🚚'} ${d.salida.fletero.nombre}` : ''}
              </span>
              {cfg ? (
                <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', background: cfg.bg, color: cfg.color, flexShrink: 0 }}>
                  {cfg.icono} {cfg.label}
                </span>
              ) : d.liberado_at ? (
                <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', background: colors.borderSoft, color: colors.textSec, flexShrink: 0 }}>
                  Liberado
                </span>
              ) : (
                <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', background: '#dbeafe', color: colors.primary, flexShrink: 0 }}>
                  {enCurso && d.salida?.estado === 'armando' ? 'En armado' : 'En viaje'}
                </span>
              )}
            </div>
            {(d.entregado_at || d.receptor_nombre) && (
              <div style={{ fontSize: 11.5, color: colors.textMeta, fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', marginTop: 3 }}>
                {d.entregado_at ? fmtFechaHora(d.entregado_at) : ''}
                {d.receptor_nombre && <span style={{ fontFamily: font.sans }}> · recibió {d.receptor_nombre}</span>}
              </div>
            )}
            {d.motivo_no_entrega && (
              <div style={{ fontSize: 11.5, color: colors.err, marginTop: 2, fontFamily: font.sans }}>
                Motivo: {d.motivo_no_entrega}
                {d.nueva_fecha && <span style={{ color: colors.textSec }}> · nueva fecha {fmtFecha(d.nueva_fecha)}</span>}
              </div>
            )}
          </div>
        )
      })}
    </Seccion>
  )
}

// Punto de estado de pago de la NV (mismo criterio que /notas-venta)
export function PagoDot({ estado }) {
  if (!estado) return null
  const map = { confirmado: '#16a34a', pendiente: '#eab308', parcial: '#f97316', rechazado: '#dc2626', devuelta: '#64748b', a_favor: '#7c3aed' }
  return <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: map[estado] || colors.textMeta, flexShrink: 0 }} />
}

// Foto chica del artículo (o una copa si no hay foto).
export function Miniatura({ codigo }) {
  const [falla, setFalla] = useState(false)
  const src = fotoArticuloUrl(codigo)
  return (
    <div style={{ width: 52, height: 52, flexShrink: 0, borderRadius: 10, border: `1px solid ${colors.borderSoft}`, background: colors.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      {src && !falla ? <img src={src} alt="" loading="lazy" onError={() => setFalla(true)} style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <span style={{ fontSize: 22, opacity: 0.35 }}>🍷</span>}
    </div>
  )
}
